import styled from '@emotion/styled'
import { useRouter } from 'found'
import React from 'react'

import { useTypedSelector } from '../../index'
import { buildPunctualityPath, resolveIntervalFromQuery } from '../../lib/wagonQueryParams'
import { getCurrentTrainParts } from '../../reducers/wagonSlice'
import { getColor } from '../../Theme'

interface Interval {
  from: string
  to: string
  index: number
  active: boolean
}

const IntervalContainer = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding: 10px;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${(p) => getColor(p.theme, ['primaryGreen'], ['grayLight'])};
    border-radius: 4px;
  }
`

const IntervalButton = styled.button<{ active: boolean }>`
  flex-shrink: 0;
  padding: 12px 16px;
  white-space: nowrap;
  cursor: pointer;
  background: ${(p) =>
    p.active
      ? getColor(p.theme, ['primaryBlue'], ['primaryBlue'])
      : getColor(p.theme, ['white'], ['grayDark'])};
  color: ${(p) =>
    p.active
      ? getColor(p.theme, ['white'], ['nightBlack'])
      : getColor(p.theme, ['primaryBlue'], ['white'])};
  border: 1px solid ${(p) => getColor(p.theme, ['primaryBlue'], ['transparent'])};
  border-radius: 4px;
`

const StationInterval = () => {
  const { router } = useRouter()
  const search = useTypedSelector((state) => state.found.match.location.search)
  const trainParts = useTypedSelector(getCurrentTrainParts)
  const trainNumber = useTypedSelector((state) => state.punctuality.selectedTrainNumber)
  const trainDate = useTypedSelector((state) => state.punctuality.selectedTrainDate)
  const activeInterval = resolveIntervalFromQuery(search, trainParts)
  const locationState = useTypedSelector((state) => state.found.match.location.state)

  if (!trainParts || trainParts.length === 0) {
    return null
  }

  const intervals: Interval[] = trainParts.map((trainPart, index) => ({
    from: trainPart.departureInfraPoint.shortCode,
    to: trainPart.arrivalInfraPoint.shortCode,
    index,
    active: activeInterval === index,
  }))

  const handleIntervalClick = (index: number) => {
    const nextPath = buildPunctualityPath(trainDate, trainNumber, { index })
    const nextUrl = new URL(nextPath, window.location.origin)
    router.replace({
      pathname: nextUrl.pathname,
      search: nextUrl.search,
      state: locationState,
    })
  }

  return (
    <IntervalContainer>
      {intervals.map((interval) => {
        const intervalId = `${interval.from}-${interval.to}`
        return (
          <IntervalButton
            key={intervalId}
            active={interval.active}
            onClick={() => handleIntervalClick(interval.index)}
          >
            {intervalId}
          </IntervalButton>
        )
      })}
    </IntervalContainer>
  )
}

export default StationInterval
